import { useCallback, useId, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { Search, X } from 'lucide-react'

import type {
  FilterDefinition,
  ListFilterId,
  ListFilterLayout,
} from '@/components/filters/list-filter-types'
import { UnifiedFilterChip } from '@/components/filters/unified-filter-chip'
import { FilterDropdownHint } from '@/components/filters/filter-dropdown-shell'
import {
  FILTER_FIELD_SHELL_CLASS,
  FILTER_HOVER_TEXT_FOREGROUND,
  FILTER_POPOVER_CONTENT_CLASS,
  UNIFIED_TEXT_SEARCH_CHIP_LABEL,
} from '@/components/filters/filter-styles'
import { buildLeadingScopeProps } from '@/components/filters/build-leading-scope-props'
import {
  ListBrowseLeadingScope,
  ListBrowseSectionTitle,
} from '@/components/filters/list-browse-toolbar'
import {
  UnifiedSuggestionList,
  UnifiedValuePicker,
} from '@/components/filters/unified-filter-dropdown'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { resolveFilterOperatorControls } from '@/lib/list-filters/unified-filter-labels'
import { formatCommaKeywordCommitHint } from '@/lib/list-filters/list-filter-params'
import {
  buildUnifiedSuggestions,
  unifiedSuggestionRowId,
  type UnifiedSuggestion,
} from '@/lib/list-filters/unified-suggestions'
import { listFilterMenuKeys, resolveListFilterLayout } from '@/lib/list-filters/resolve-list-filter-layout'
import { resolveLeadFilterDef } from '@/lib/list-filters/resolve-filter-defs'
import {
  appendChipKey,
  removeChipKey,
  TEXT_SEARCH_CHIP_KEY,
} from '@/lib/list-filters/unified-chip-order'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useUnifiedChipOrder } from '@/hooks/use-unified-chip-order'
import { useUnifiedSearchDraft } from '@/hooks/use-unified-search-draft'
import {
  resolveFilterTypeaheadQuery,
  resolveInlineInputValue,
  resolveInlineTextHandoffDraft,
  shouldCollapseInlineInput,
} from '@/lib/list-filters/unified-filter-keyword'
import { normalizeCommaSeparatedTextQuery } from '@/lib/list-filters/list-filter-params'
import { cn } from '@/lib/utils'

const UNIFIED_TYPEAHEAD_DEBOUNCE_MS = 200

function syncChipOrderForFilterChange(
  wasActive: boolean,
  isActive: boolean,
  key: string,
  setChipOrder: Dispatch<SetStateAction<string[]>>,
) {
  if (!wasActive && isActive) {
    setChipOrder((prev) => appendChipKey(prev, key))
  } else if (wasActive && !isActive) {
    setChipOrder((prev) => removeChipKey(prev, key))
  }
}

type UnifiedMode =
  | { name: 'closed' }
  | { name: 'suggest' }
  | { name: 'picker'; key: string; origin: 'suggest' | 'chip' }
  | { name: 'inlineText'; key: string; origin: 'suggest' | 'chip' }

export type UnifiedFilterBarSearchProps = {
  searchId?: string
  searchPlaceholder: string
  /** In-progress draft. Omit (with `onSearchChange`) to keep draft state inside the bar. */
  searchValue?: string
  onSearchChange?: (value: string) => void
  /** Commit draft to the list query (Enter, outside click, Tab). */
  onSearchBlur?: (value: string) => void
  /** Committed keyword search shown as a “Keywords contain” chip; `searchValue` holds the draft until commit. */
  committedSearchValue?: string
  /** Operator word on the keyword chip label segment (e.g. “contain”, “do not contain”). */
  committedSearchOperator?: string
  /** Toggle contain ↔ do not contain without opening the dropdown. */
  onToggleCommittedSearchOperator?: () => void
  /** Clears committed search when the chip is removed or inline search is cleared. */
  onClearCommittedSearch?: () => void
  /**
   * `inline` (default): keyword query lives in the text input (search-box model).
   * `chip`: committed keyword renders as a muted chip (legacy Shopify-style).
   */
  keywordDisplay?: 'inline' | 'chip'
  /** Override committed keyword display on the chip value segment. */
  formatCommittedSearchDisplay?: (value: string) => string
  /** Override the dropdown hint shown when pressing Enter to commit a plain search. */
  formatSearchCommitHint?: (query: string) => string
  /** Inline section label (e.g. “Jobs”), aligned with the field as one row. */
  sectionTitle?: string
}

/** Optional trailing slots — fixed order: view · sort · extra. */
export type UnifiedFilterBarToolbarSlots = {
  viewSwitcher?: ReactNode
  sortMenu?: ReactNode
  trailingExtra?: ReactNode
}

export type UnifiedFilterBarProps<TFilters, TCtx> = {
  listId: ListFilterId
  filters: TFilters
  onFiltersChange: (next: TFilters) => void
  definitions: FilterDefinition<TFilters, TCtx>[]
  ctx: TCtx
  search: UnifiedFilterBarSearchProps
  /** Renders an "Ask about …" row for free text; v1 slot — wiring is per page. */
  onSemanticSearch?: (text: string) => void
  layout?: ListFilterLayout
  toolbar?: UnifiedFilterBarToolbarSlots
  hasActiveFilters?: boolean
  onClearAll?: () => void
  className?: string
  disabled?: boolean
}

/**
 * Shopify-admin-style unified search + filter field: one field-styled
 * container holding applied filter chips and a borderless text input. Typing
 * drives the page text search AND typeahead filter suggestions; a single
 * Radix Popover flips between the suggestion list and a value picker.
 *
 * Focus rule: DOM focus never leaves the input while the dropdown is open —
 * rows highlight via aria-activedescendant and use onMouseDown.preventDefault.
 * Refocusing the field (or clicking it while in a value picker) returns to the
 * category list; chip clicks still open that chip's picker. Committed keyword search (`q`)
 * is independent from filter typeahead — picking a filter consumes the draft, not `q`.
 */
export function UnifiedFilterBar<TFilters, TCtx>({
  listId,
  filters,
  onFiltersChange,
  definitions,
  ctx,
  search,
  onSemanticSearch,
  layout: layoutOverride,
  toolbar,
  hasActiveFilters = false,
  onClearAll,
  className,
  disabled,
}: UnifiedFilterBarProps<TFilters, TCtx>) {
  const listboxId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const popoverContentRef = useRef<HTMLDivElement>(null)

  const [mode, setMode] = useState<UnifiedMode>({ name: 'closed' })
  const [activeIndex, setActiveIndex] = useState(-1)
  const [fieldHovered, setFieldHovered] = useState(false)
  const [inlineEditing, setInlineEditing] = useState(false)
  const [leadScopeOpen, setLeadScopeOpen] = useState(false)

  const {
    searchId = 'unified-filter-search',
    searchPlaceholder,
    searchValue,
    onSearchChange,
    onSearchBlur,
    committedSearchValue,
    onClearCommittedSearch,
    formatCommittedSearchDisplay,
    formatSearchCommitHint,
    sectionTitle,
    keywordDisplay = 'inline',
  } = search

  const { draftValue: draftSearchValue, setDraftValue: setDraftSearchValue } = useUnifiedSearchDraft({
    searchValue,
    onSearchChange,
  })

  const committedText = committedSearchValue?.trim() ?? ''
  const showTextSearchChip =
    keywordDisplay === 'chip' && committedText.length > 0 && draftSearchValue.length === 0
  const committedTextDisplay = formatCommittedSearchDisplay
    ? formatCommittedSearchDisplay(committedText)
    : committedText

  const inputValue = resolveInlineInputValue({
    mode,
    keywordDisplay,
    committedText,
    keywordDraft: draftSearchValue,
    inlineEditing,
  })

  const rawTypeaheadQuery = resolveFilterTypeaheadQuery({
    mode,
    keywordDraft: draftSearchValue,
  })

  const typeaheadQuery = useDebouncedValue(rawTypeaheadQuery, UNIFIED_TYPEAHEAD_DEBOUNCE_MS)

  const layout = useMemo(
    () => layoutOverride ?? resolveListFilterLayout(listId),
    [layoutOverride, listId],
  )

  const leadDef = resolveLeadFilterDef(layout.lead, definitions, ctx)

  const orderedKeys = useMemo(
    () => listFilterMenuKeys(layout),
    [layout],
  )

  const suggestions = useMemo(
    () =>
      mode.name === 'closed' || mode.name === 'inlineText'
        ? []
        : buildUnifiedSuggestions(typeaheadQuery, definitions, filters, ctx, {
            orderedKeys,
            includeSemantic: Boolean(onSemanticSearch),
          }),
    [mode.name, typeaheadQuery, definitions, filters, ctx, orderedKeys, onSemanticSearch],
  )

  const { defs: appliedDefs, keys: appliedFilterKeys, byKey: appliedDefsByKey } = useMemo(() => {
    const defs = definitions.filter((def) => def.key !== layout.lead && def.isNonDefault(filters, ctx))
    return {
      defs,
      keys: defs.map((def) => def.key),
      byKey: new Map(defs.map((def) => [def.key, def])),
    }
  }, [definitions, filters, ctx, layout.lead])

  const { setChipOrder, orderedVisibleKeys: orderedVisibleChipKeys } = useUnifiedChipOrder(
    showTextSearchChip,
    appliedFilterKeys,
    orderedKeys,
  )

  const pickerDef =
    mode.name === 'picker' ? definitions.find((def) => def.key === mode.key) : undefined
  const inlineTextDef =
    mode.name === 'inlineText' ? definitions.find((def) => def.key === mode.key) : undefined
  const inlineTextLabel = inlineTextDef?.label

  const open = mode.name !== 'closed'
  const hasChips = appliedDefs.length > 0 || showTextSearchChip
  const hasClearableState =
    hasChips ||
    hasActiveFilters ||
    draftSearchValue.trim().length > 0 ||
    (keywordDisplay === 'inline' && committedText.length > 0)
  /** No chips and no search text — center icon with placeholder. */
  const isFieldEmpty =
    !hasChips &&
    (keywordDisplay === 'inline'
      ? committedText.length === 0 && draftSearchValue.trim().length === 0
      : draftSearchValue.trim().length === 0)
  const fieldEngaged = open || fieldHovered
  const reserveClearSpace = hasClearableState && !isFieldEmpty
  const collapseInlineInput = shouldCollapseInlineInput({
    keywordDisplay,
    hasChips,
    mode,
    keywordDraft: draftSearchValue,
    inlineEditing,
    committedText,
    dropdownOpen: open,
  })

  const closeDropdown = useCallback(() => {
    setMode({ name: 'closed' })
    setActiveIndex(-1)
  }, [])

  const handleLeadScopeOpenChange = useCallback(
    (open: boolean) => {
      setLeadScopeOpen(open)
      if (open) closeDropdown()
    },
    [closeDropdown],
  )

  const handleLeadClose = useCallback(() => setLeadScopeOpen(false), [])

  const openSuggest = useCallback(() => {
    setMode({ name: 'suggest' })
    setActiveIndex(-1)
  }, [])

  /** Return to the category list without touching keyword draft or committed search. */
  const openCategoryList = useCallback(() => {
    if (mode.name !== 'inlineText') {
      setInlineEditing(false)
    }
    openSuggest()
  }, [mode.name, openSuggest])

  const beginInlineTextFilterEdit = useCallback(
    (def: FilterDefinition<TFilters, TCtx>, initialValue?: string, origin: 'suggest' | 'chip' = 'suggest') => {
      const meta = def.unified
      if (!meta) return
      const current = initialValue ?? meta.getSelectedValues(filters, ctx)[0] ?? ''
      setInlineEditing(false)
      setDraftSearchValue(current)
      setMode({ name: 'inlineText', key: def.key, origin })
      setActiveIndex(-1)
      requestAnimationFrame(() => inputRef.current?.focus())
    },
    [filters, ctx, setDraftSearchValue],
  )

  const commitInlineTextFilter = useCallback(
    (value: string) => {
      if (!inlineTextDef?.unified) return
      const trimmed = normalizeCommaSeparatedTextQuery(value)
      const wasActive = inlineTextDef.isNonDefault(filters, ctx)
      const next = trimmed
        ? inlineTextDef.unified.setSelectedValues(filters, [trimmed], ctx)
        : inlineTextDef.reset(filters)
      const isActive = inlineTextDef.isNonDefault(next, ctx)

      onFiltersChange(next)
      syncChipOrderForFilterChange(wasActive, isActive, inlineTextDef.key, setChipOrder)
      setDraftSearchValue('')
      closeDropdown()
    },
    [inlineTextDef, filters, ctx, onFiltersChange, setDraftSearchValue, closeDropdown],
  )

  const commitTextSearch = useCallback(
    (value: string) => {
      if (mode.name === 'inlineText') {
        commitInlineTextFilter(value)
        return
      }

      if (keywordDisplay === 'inline') {
        setInlineEditing(false)
        const trimmed = value.trim()
        if (trimmed) {
          onSearchBlur?.(value)
        } else {
          onClearCommittedSearch?.()
        }
        setDraftSearchValue('')
        closeDropdown()
        return
      }

      if (value.trim()) {
        onSearchBlur?.(value)
        setChipOrder((prev) => appendChipKey(prev, TEXT_SEARCH_CHIP_KEY))
        setDraftSearchValue('')
      }
      closeDropdown()
    },
    [
      mode.name,
      keywordDisplay,
      commitInlineTextFilter,
      onSearchBlur,
      onClearCommittedSearch,
      setDraftSearchValue,
      closeDropdown,
    ],
  )

  const cancelInlineEdit = useCallback(() => {
    setInlineEditing(false)
    setDraftSearchValue('')
  }, [setDraftSearchValue])

  const removeCommittedTextSearch = useCallback(() => {
    setChipOrder((prev) => removeChipKey(prev, TEXT_SEARCH_CHIP_KEY))
    onClearCommittedSearch?.()
  }, [onClearCommittedSearch])

  const beginEditCommittedTextSearch = useCallback(() => {
    setInlineEditing(true)
    setDraftSearchValue(committedText)
    setMode({ name: 'suggest' })
    setActiveIndex(-1)
    inputRef.current?.focus()
  }, [committedText, setDraftSearchValue])

  const applySuggestion = useCallback(
    (suggestion: UnifiedSuggestion<TFilters, TCtx>) => {
      const clearTypeaheadDraft = () => {
        setDraftSearchValue('')
        setInlineEditing(false)
      }

      if (suggestion.kind === 'category') {
        if (suggestion.def.unified?.valueEntry === 'inlineText') {
          const meta = suggestion.def.unified
          const existing = meta.getSelectedValues(filters, ctx)[0] ?? ''
          const handoff = resolveInlineTextHandoffDraft(
            draftSearchValue,
            suggestion.def.label,
            existing,
          )
          beginInlineTextFilterEdit(suggestion.def, handoff, 'suggest')
          return
        }
        clearTypeaheadDraft()
        setMode({ name: 'picker', key: suggestion.def.key, origin: 'suggest' })
        setActiveIndex(-1)
        return
      }
      if (suggestion.kind === 'value') {
        const meta = suggestion.def.unified
        if (!meta) return
        const wasActive = suggestion.def.isNonDefault(filters, ctx)
        const selected = meta.getSelectedValues(filters, ctx)
        const nextValues =
          meta.selection === 'multi' ? [...selected, suggestion.option.value] : [suggestion.option.value]
        onFiltersChange(meta.setSelectedValues(filters, nextValues, ctx))
        if (!wasActive) {
          setChipOrder((prev) => appendChipKey(prev, suggestion.def.key))
        }
        clearTypeaheadDraft()
        if (meta.selection === 'multi') {
          setMode({ name: 'picker', key: suggestion.def.key, origin: 'suggest' })
          setActiveIndex(-1)
        } else {
          openCategoryList()
        }
        return
      }
      clearTypeaheadDraft()
      onSemanticSearch?.(suggestion.query)
      closeDropdown()
    },
    [
      filters,
      ctx,
      draftSearchValue,
      onFiltersChange,
      setDraftSearchValue,
      onSemanticSearch,
      closeDropdown,
      beginInlineTextFilterEdit,
      openCategoryList,
    ],
  )

  const removeFilter = useCallback(
    (def: FilterDefinition<TFilters, TCtx>) => {
      setChipOrder((prev) => removeChipKey(prev, def.key))
      onFiltersChange(def.reset(filters))
      if (mode.name === 'picker' && mode.key === def.key) openSuggest()
    },
    [filters, onFiltersChange, mode, openSuggest],
  )

  const handleClearAll = useCallback(() => {
    setChipOrder([])
    setDraftSearchValue('')
    setInlineEditing(false)
    onClearCommittedSearch?.()
    onClearAll?.()
    closeDropdown()
  }, [setDraftSearchValue, onClearCommittedSearch, onClearAll, closeDropdown])

  const removeLastChip = useCallback(() => {
    const last = orderedVisibleChipKeys[orderedVisibleChipKeys.length - 1]
    if (!last) return
    if (last === TEXT_SEARCH_CHIP_KEY) {
      removeCommittedTextSearch()
      return
    }
    const def = appliedDefsByKey.get(last)
    if (def) removeFilter(def)
  }, [orderedVisibleChipKeys, appliedDefsByKey, removeFilter, removeCommittedTextSearch])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (mode.name === 'inlineText') {
        if (event.key === 'Enter') {
          event.preventDefault()
          commitInlineTextFilter(inputValue)
          return
        }
        if (event.key === 'Escape') {
          // Cancel: previous committed value (or absence) is untouched.
          event.preventDefault()
          cancelInlineEdit()
          closeDropdown()
          return
        }
        if (event.key === 'Backspace' && inputValue === '') {
          event.preventDefault()
          cancelInlineEdit()
          openSuggest()
          return
        }
        if (event.key === 'Tab') {
          commitInlineTextFilter(inputValue)
          return
        }
        return
      }

      if (event.key === 'Escape') {
        if (keywordDisplay === 'inline') {
          cancelInlineEdit()
        }
        if (mode.name === 'picker') {
          event.preventDefault()
          if (mode.origin === 'chip') closeDropdown()
          else openSuggest()
        } else if (mode.name === 'suggest') {
          event.preventDefault()
          closeDropdown()
        }
        return
      }

      if (event.key === 'Tab' && mode.name !== 'closed') {
        commitTextSearch(keywordDisplay === 'inline' ? inputValue : draftSearchValue)
        return
      }

      if (event.key === 'Backspace' && inputValue === '' && hasChips) {
        event.preventDefault()
        removeLastChip()
        return
      }

      if (mode.name === 'closed') {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          openSuggest()
        }
        return
      }

      if (mode.name === 'suggest') {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault()
          if (suggestions.length === 0) return
          setActiveIndex((current) => {
            const delta = event.key === 'ArrowDown' ? 1 : -1
            const next = current + delta
            if (next < -1) return suggestions.length - 1
            if (next >= suggestions.length) return -1
            return next
          })
          return
        }
        if (event.key === 'Enter') {
          event.preventDefault()
          if (activeIndex >= 0 && activeIndex < suggestions.length) {
            applySuggestion(suggestions[activeIndex])
          } else {
            commitTextSearch(keywordDisplay === 'inline' ? inputValue : draftSearchValue)
          }
        }
      }
    },
    [
      mode,
      keywordDisplay,
      inputValue,
      draftSearchValue,
      hasChips,
      suggestions,
      activeIndex,
      applySuggestion,
      commitTextSearch,
      commitInlineTextFilter,
      cancelInlineEdit,
      closeDropdown,
      openSuggest,
      removeLastChip,
    ],
  )

  const handleInputChange = useCallback(
    (value: string) => {
      setDraftSearchValue(value)
      if (mode.name === 'inlineText') {
        return
      }
      if (keywordDisplay === 'inline') {
        setInlineEditing(true)
      }
      setMode({ name: 'suggest' })
      // Keep keyword search as the default Enter action — filters are opt-in via click/arrows.
      setActiveIndex(-1)
    },
    [keywordDisplay, mode.name, setDraftSearchValue],
  )

  const searchCommitHint = useMemo(() => {
    const trimmed = inputValue.trim()
    if (!trimmed) return undefined
    const hint = formatSearchCommitHint ?? formatCommaKeywordCommitHint
    return hint(trimmed)
  }, [inputValue, formatSearchCommitHint])

  const handlePickerFiltersChange = useCallback(
    (next: TFilters) => {
      if (!pickerDef) {
        onFiltersChange(next)
        return
      }
      const wasActive = pickerDef.isNonDefault(filters, ctx)
      const isActive = pickerDef.isNonDefault(next, ctx)
      onFiltersChange(next)
      syncChipOrderForFilterChange(wasActive, isActive, pickerDef.key, setChipOrder)
    },
    [pickerDef, filters, ctx, onFiltersChange],
  )

  const popoverProps = useMemo(
    () => ({ filters, onChange: handlePickerFiltersChange, ctx }),
    [filters, handlePickerFiltersChange, ctx],
  )

  const dismissPopoverInteraction = useCallback((event: Event) => {
    const target = event.target
    if (!(target instanceof Node)) return false
    if (anchorRef.current?.contains(target)) return true
    // Focus stays on the combobox input (modal={false}); Radix can treat portaled
    // picker clicks as "outside" unless we explicitly keep the popover open.
    if (popoverContentRef.current?.contains(target)) return true
    return false
  }, [])

  const showControls = Boolean(toolbar?.viewSwitcher || toolbar?.sortMenu || toolbar?.trailingExtra)

  const leadingScope = useMemo(
    () =>
      buildLeadingScopeProps({
        leadDef,
        filters,
        ctx,
        onFiltersChange,
        open: leadScopeOpen,
        onOpenChange: handleLeadScopeOpenChange,
        onClose: handleLeadClose,
        disabled,
        density: 'unified',
      }),
    [
      leadDef,
      filters,
      ctx,
      onFiltersChange,
      leadScopeOpen,
      handleLeadScopeOpenChange,
      handleLeadClose,
      disabled,
    ],
  )

  return (
    <div className={cn('flex flex-wrap items-start gap-x-2 gap-y-1', className)}>
      {sectionTitle ? <ListBrowseSectionTitle title={sectionTitle} /> : null}

      <Popover open={open} modal={false}>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            className={cn(
              'group/field relative flex gap-1.5 px-2.5 py-[3px]',
              FILTER_FIELD_SHELL_CLASS,
              isFieldEmpty || collapseInlineInput ? 'items-center' : 'items-start',
              reserveClearSpace && 'pr-8',
              disabled && 'pointer-events-none opacity-50',
            )}
            onMouseEnter={() => setFieldHovered(true)}
            onMouseLeave={() => setFieldHovered(false)}
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('[data-leading-scope]')) return
              if ((event.target as HTMLElement).closest('[data-unified-chip]')) return
              if (mode.name === 'inlineText') {
                inputRef.current?.focus()
                return
              }
              openCategoryList()
              inputRef.current?.focus()
            }}
          >
            {leadingScope ? <ListBrowseLeadingScope {...leadingScope} /> : null}

            <div
              className={cn(
                'flex h-6.5 shrink-0 items-center',
                isFieldEmpty || collapseInlineInput ? 'self-center' : 'self-start',
              )}
            >
              <Search className="pointer-events-none h-4 w-4 text-muted-foreground" aria-hidden />
            </div>

            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
              {orderedVisibleChipKeys.map((chipKey) => {
                if (chipKey === TEXT_SEARCH_CHIP_KEY) {
                  if (!showTextSearchChip) return null
                  return (
                    <UnifiedFilterChip
                      key={TEXT_SEARCH_CHIP_KEY}
                      variant="text"
                      label={UNIFIED_TEXT_SEARCH_CHIP_LABEL}
                      value={committedTextDisplay}
                      valueTitle={committedText !== committedTextDisplay ? committedText : undefined}
                      onClick={beginEditCommittedTextSearch}
                      onRemove={removeCommittedTextSearch}
                    />
                  )
                }

                const def = appliedDefsByKey.get(chipKey)
                if (!def) return null
                // While a text filter is inline-edited, the label pill + input
                // replace its chip — never show both representations at once.
                if (mode.name === 'inlineText' && mode.key === def.key) return null

                const controls = resolveFilterOperatorControls(def)

                return (
                  <UnifiedFilterChip
                    key={def.key}
                    label={def.label}
                    operator={
                      controls ? controls.formatChip(controls.getOperator(filters)) : undefined
                    }
                    nextOperatorLabel={
                      controls
                        ? controls.nextOperator(controls.getOperator(filters))
                        : undefined
                    }
                    value={def.displayValue(filters, ctx)}
                    valueTitle={def.chipValueTitle?.(filters, ctx)}
                    onClick={() => {
                      inputRef.current?.focus()
                      if (def.unified?.valueEntry === 'inlineText') {
                        beginInlineTextFilterEdit(def, undefined, 'chip')
                        return
                      }
                      setMode({ name: 'picker', key: def.key, origin: 'chip' })
                      setActiveIndex(-1)
                    }}
                    onToggleOperator={
                      controls
                        ? () => {
                            closeDropdown()
                            const current = controls.getOperator(filters)
                            const nextIndex =
                              (controls.operators.indexOf(current) + 1) % controls.operators.length
                            onFiltersChange(
                              controls.setOperator(filters, controls.operators[nextIndex]),
                            )
                          }
                        : undefined
                    }
                    onRemove={() => removeFilter(def)}
                  />
                )
              })}

              {inlineTextLabel ? (
                <span className="flex h-6.5 shrink-0 items-center rounded-[var(--radius-button)] bg-primary/10 px-2.5 text-xs font-medium text-muted-foreground">
                  {inlineTextLabel}
                </span>
              ) : null}

              <input
                ref={inputRef}
                id={searchId}
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-activedescendant={
                  mode.name === 'suggest' && activeIndex >= 0
                    ? unifiedSuggestionRowId(listboxId, activeIndex)
                    : undefined
                }
                aria-autocomplete="list"
                aria-label={searchPlaceholder}
                placeholder={
                  mode.name === 'inlineText'
                    ? inlineTextDef?.unified?.inlineTextPlaceholder ??
                      `Type ${inlineTextLabel?.toLowerCase() ?? 'value'}…`
                    : keywordDisplay === 'inline'
                    ? !inputValue.trim() && !hasChips
                      ? searchPlaceholder
                      : undefined
                    : !hasChips
                      ? searchPlaceholder
                      : undefined
                }
                value={inputValue}
                disabled={disabled}
                onChange={(event) => handleInputChange(event.target.value)}
                onFocus={() => {
                  if (mode.name === 'inlineText') return
                  if (mode.name === 'closed' || mode.name === 'picker') {
                    openCategoryList()
                  }
                }}
                onBlur={() => {
                  if (mode.name === 'inlineText') {
                    commitInlineTextFilter(inputValue)
                  }
                }}
                onKeyDown={handleKeyDown}
                className={cn(
                  'h-6.5 bg-transparent text-sm outline-none placeholder:text-muted-foreground',
                  keywordDisplay === 'inline'
                    ? collapseInlineInput
                      ? 'w-0 min-w-0 flex-none'
                      : 'min-w-[6rem] flex-1'
                    : hasChips && !open && draftSearchValue === ''
                      ? 'w-0 min-w-0 flex-none'
                      : hasChips
                        ? 'min-w-12 flex-1'
                        : 'min-w-[6rem] flex-1',
                )}
                autoComplete="off"
              />
            </div>

            {reserveClearSpace ? (
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClearAll}
                className={cn(
                  'absolute right-2.5 top-2 shrink-0 text-muted-foreground',
                  'transition-[opacity,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
                  'active:scale-[0.97] motion-reduce:active:scale-100',
                  FILTER_HOVER_TEXT_FOREGROUND,
                  'pointer-events-none opacity-0',
                  fieldEngaged && 'pointer-events-auto opacity-100',
                  'group-focus-within/field:pointer-events-auto group-focus-within/field:opacity-100',
                )}
                aria-label="Clear search and filters"
                tabIndex={fieldEngaged ? 0 : -1}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>
        </PopoverAnchor>

        <PopoverContent
          ref={popoverContentRef}
          align="start"
          sideOffset={4}
          className={cn(
            'w-80 max-w-[min(100vw-2rem,20rem)] p-0',
            'rounded-lg border-border/50 bg-popover shadow-lg',
            FILTER_POPOVER_CONTENT_CLASS,
          )}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            if (dismissPopoverInteraction(event)) {
              event.preventDefault()
            }
          }}
          onInteractOutside={(event) => {
            if (dismissPopoverInteraction(event)) {
              event.preventDefault()
              return
            }
            commitTextSearch(keywordDisplay === 'inline' ? inputValue : draftSearchValue)
          }}
        >
          {mode.name === 'inlineText' && inlineTextDef ? (
            <FilterDropdownHint
              hint="Press Enter to apply · Esc to cancel"
            >
              {inputValue.trim() ? (
                <>
                  Filter to {inlineTextDef.label.toLowerCase()} containing{' '}
                  <span className="font-semibold text-foreground">&ldquo;{inputValue.trim()}&rdquo;</span>
                </>
              ) : inlineTextDef.isNonDefault(filters, ctx) ? (
                <>Clear the text to remove this filter</>
              ) : (
                <>Type a {inlineTextDef.label.toLowerCase()} to filter by</>
              )}
            </FilterDropdownHint>
          ) : mode.name === 'picker' && pickerDef ? (
            <UnifiedValuePicker
              def={pickerDef}
              popoverProps={popoverProps}
              pickerOrigin={mode.origin}
              onBack={() => {
                if (mode.origin === 'chip') closeDropdown()
                else openSuggest()
                inputRef.current?.focus()
              }}
              onSinglePicked={() => {
                if (mode.origin === 'chip') closeDropdown()
                else openCategoryList()
              }}
            />
          ) : (
            <UnifiedSuggestionList
              listboxId={listboxId}
              suggestions={suggestions}
              activeIndex={activeIndex}
              filters={filters}
              searchCommitHint={searchCommitHint}
              onPick={(suggestion) => applySuggestion(suggestion)}
              onHover={(index) => setActiveIndex((current) => (current === index ? current : index))}
            />
          )}
        </PopoverContent>
      </Popover>

      {showControls ? (
        <div className="flex shrink-0 flex-wrap items-start justify-end gap-1 self-start mt-1">
          {toolbar?.viewSwitcher}
          {toolbar?.sortMenu}
          {toolbar?.trailingExtra}
        </div>
      ) : null}
    </div>
  )
}
